import type { Lane, Sentiment } from "@/components/feedback-dialog/schemas"

import { api } from "@/lib/api-client"

export type FeedbackContext = {
  error_id?: string | null
  session_id?: string | null
  message_id?: string | null
  response_content?: string | null
  /**
   * Correlation id of the triggering error / last response. Lets the backend pull
   * the matching server-log excerpt from the diagnostics ring buffer (#2152).
   */
  correlation_id?: string | null
  /** Cached `/api/config` response (whatever the app was using right then). */
  app_config_snapshot?: unknown
  /** Subset of the current MSAL claims the frontend has access to. */
  identity_snapshot?: {
    oid?: string | null
    tid?: string | null
    name?: string | null
    preferred_username?: string | null
    email?: string | null
    [key: string]: unknown
  }
  client_info?: {
    /** Current URL. */
    url: string
    /** `window.location.pathname` — the route part of the URL, no host/query. */
    route?: string
    /** User-Agent string. */
    user_agent: string
    /** ISO timestamp at submit time. */
    timestamp: string
    /** Inner window dimensions, e.g. "1440x900". */
    viewport: string
    /** navigator.language. */
    language: string
    /** document.title at submit time. */
    page_title?: string
    /** document.referrer, if any. */
    referrer?: string
    /** IANA timezone name (e.g. "Europe/Stockholm"). */
    timezone?: string
    /** Physical screen resolution, e.g. "2560x1440". */
    screen_resolution?: string
    /** window.devicePixelRatio (1, 2, 3, …). */
    device_pixel_ratio?: number
    /** navigator.onLine. */
    online?: boolean
    /** Effective connection type from navigator.connection, e.g. "4g". */
    connection_type?: string
    /** navigator.platform (deprecated but still informative). */
    platform?: string
    /** "dark" or "light" from prefers-color-scheme. */
    color_scheme?: "dark" | "light"
    /** prefers-reduced-motion is active. */
    reduced_motion?: boolean
    /** navigator.cookieEnabled. */
    cookies_enabled?: boolean
    /** Hardware concurrency (logical CPUs). */
    hardware_concurrency?: number
    /** Approximate device memory in GB (navigator.deviceMemory). */
    device_memory?: number
  }
}

export type FeedbackSubmitRequest = {
  lane: Lane
  fields: Record<string, unknown>
  context?: FeedbackContext
  /**
   * User's consent to escalate, for lanes/fields where escalation is
   * eligible. Omit when not applicable (response_feedback, or a bug that
   * doesn't meet the eligibility criteria) — the backend ignores it unless
   * the report is eligible.
   */
  escalate_requested?: boolean
}

export type FeedbackSubmitResponse = {
  feedback_id: string
  log_id: string | null
  lane: Lane
  escalated: boolean
  escalation_reasons: string[]
  email_sent: boolean
  email_error?: string
}

export type EvidenceUploadResponse = {
  blob_path: string
  content_type: string
  size: number
}

type ClientInfo = NonNullable<FeedbackContext["client_info"]>

/**
 * Gather everything useful we can detect from the browser at submit time.
 * All fields are best-effort — missing APIs degrade silently.
 */
export function buildClientInfo(): ClientInfo {
  const info: ClientInfo = {
    url: window.location.href,
    route: window.location.pathname,
    user_agent: navigator.userAgent,
    timestamp: new Date().toISOString(),
    viewport: `${window.innerWidth}x${window.innerHeight}`,
    language: navigator.language,
  }

  // Title + referrer
  if (typeof document !== "undefined") {
    if (document.title) info.page_title = document.title
    if (document.referrer) info.referrer = document.referrer
  }

  // Timezone
  try {
    info.timezone = Intl.DateTimeFormat().resolvedOptions().timeZone
  } catch {
    /* noop */
  }

  // Screen + DPI
  if (typeof window !== "undefined") {
    info.screen_resolution = `${window.screen.width}x${window.screen.height}`
    info.device_pixel_ratio = window.devicePixelRatio
  }

  // Network-ish bits
  info.online = navigator.onLine
  const connection = (navigator as Navigator & { connection?: { effectiveType?: string } })
    .connection
  if (connection?.effectiveType) info.connection_type = connection.effectiveType

  // Platform / capabilities
  if (navigator.platform) info.platform = navigator.platform
  info.cookies_enabled = navigator.cookieEnabled
  if (typeof navigator.hardwareConcurrency === "number") {
    info.hardware_concurrency = navigator.hardwareConcurrency
  }
  const deviceMemory = (navigator as Navigator & { deviceMemory?: number }).deviceMemory
  if (typeof deviceMemory === "number") info.device_memory = deviceMemory

  // Preferences
  try {
    info.color_scheme = window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light"
    info.reduced_motion = window.matchMedia("(prefers-reduced-motion: reduce)").matches
  } catch {
    /* noop */
  }

  return info
}

export async function submitFeedback(
  payload: FeedbackSubmitRequest
): Promise<FeedbackSubmitResponse> {
  const baseClientInfo = buildClientInfo()
  // Caller may have supplied a pre-built client_info (e.g. captured at dialog
  // open). We merge — anything they provided wins, anything they omitted is
  // filled in now.
  const enrichedContext: FeedbackContext = {
    ...payload.context,
    client_info: {
      ...baseClientInfo,
      ...(payload.context?.client_info ?? {}),
    },
  }
  return api.post("/feedback", {
    lane: payload.lane,
    fields: payload.fields,
    context: enrichedContext,
    escalate_requested: payload.escalate_requested,
  })
}

export async function uploadFeedbackEvidence(file: File): Promise<EvidenceUploadResponse> {
  const formData = new FormData()
  formData.append("file", file)
  return api.post("/feedback/evidence", formData)
}

// -----------------------------------------------------------------------------
// Admin: list / detail / hydrate feedback records
// -----------------------------------------------------------------------------

export type AdminFeedbackSummary = {
  id: string
  feedback_id: string | null
  lane: Lane | null
  escalated: boolean
  escalation_reasons: string[]
  submitted_at: string | null
  user: { id?: string; name?: string }
  preview:
    | { sentiment?: Sentiment; comment?: string | null }
    | { severity?: string; whoAffected?: string; whatTrying?: string | null }
    | { summary?: string | null; timeframe?: string | null }
    | null
}

export type AdminFeedbackListResponse = {
  items: AdminFeedbackSummary[]
}

/**
 * A single sanitized server-log line captured at feedback-submit time, pulled
 * from the in-process ring buffer and matched by `correlation_id`/`session_id`.
 * The message is the *formatted* log line only — never raw record args, prompts,
 * or document contents. `stack_summary` is a bounded frames-only traceback tail
 * (no locals). See `routes/feedback_enrichment.py::_build_server_logs`.
 */
export type ServerLogEntry = {
  /** ISO-8601 UTC timestamp. */
  timestamp?: string
  /** Python log level name, e.g. "ERROR", "WARNING", "INFO". */
  severity?: string
  /** Originating logger name (dotted module path). */
  logger?: string
  /** Formatted log message. */
  message?: string
  /** Correlation id the line was tagged with ("-" when absent). */
  correlation_id?: string
  /** Session id the line was tagged with ("-" when absent). */
  session_id?: string
  /** Exception class name when the record carried exc_info, else null. */
  exception_type?: string | null
  /** Bounded frames-only traceback tail, else null. */
  stack_summary?: string | null
}

/**
 * Server-log slice attached to a feedback record (Wave 2). Optional for
 * back-compat: records submitted before server-log capture shipped lack this
 * field entirely. When present but `entries` is empty, `unavailable_reason`
 * always carries a human-readable explanation (capture never silently omits).
 */
export type ServerLogs = {
  entries: ServerLogEntry[]
  /** Capture source, currently always "ring_buffer". */
  source?: string
  /** Human reason when `entries` is empty (size budget, no logs, etc.). */
  unavailable_reason?: string | null
}

export type AdminFeedbackDetail = {
  item: Record<string, unknown> & {
    id?: string
    type?: string
    content?: Record<string, unknown>
  }
  has_evidence: boolean
  has_screenshot: boolean
}

export type FeedbackAttachmentKind = "evidence" | "screenshot"

export async function listAdminFeedback(
  params: { lane?: Lane; limit?: number } = {}
): Promise<AdminFeedbackListResponse> {
  const search = new URLSearchParams()
  if (params.lane) search.set("lane", params.lane)
  if (params.limit) search.set("limit", String(params.limit))
  const qs = search.toString()
  return api.get(`/admin/feedback${qs ? `?${qs}` : ""}`)
}

export async function getAdminFeedback(id: string): Promise<AdminFeedbackDetail> {
  return api.get(`/admin/feedback/${encodeURIComponent(id)}`)
}

export async function getAdminFeedbackAttachment(
  id: string,
  kind: FeedbackAttachmentKind,
  signal?: AbortSignal
): Promise<Blob> {
  return api.get(`/admin/feedback/${encodeURIComponent(id)}/attachment/${kind}`, {
    responseType: "blob",
    signal,
  }) as unknown as Promise<Blob>
}

export async function hydrateAdminFeedback(id: string): Promise<{ session_context: unknown }> {
  return api.post(`/admin/feedback/${encodeURIComponent(id)}/hydrate`, {})
}

// -----------------------------------------------------------------------------
// Admin: aggregated analytics for the Feedback section on the dashboard
// -----------------------------------------------------------------------------

export type FeedbackVolumePoint = {
  date: string // yyyy-mm-dd
  up: number
  down: number
  bug: number
  incident: number
}

export type FeedbackEscalationPoint = {
  date: string
  bugs: number
  escalated: number
  rate: number // 0..1
}

export type FeedbackStatsResponse = {
  days: number
  volume_series: FeedbackVolumePoint[]
  top_pages: Array<{ page: string; count: number }>
  severity_mix: Array<{ severity: string; count: number }>
  escalation: {
    total_bugs: number
    escalated_bugs: number
    rate: number
    series: FeedbackEscalationPoint[]
  }
}

export async function getAdminFeedbackStats(days = 30): Promise<FeedbackStatsResponse> {
  return api.get(`/admin/feedback/stats?days=${encodeURIComponent(String(days))}`)
}

// -----------------------------------------------------------------------------
// Convenience helpers
// -----------------------------------------------------------------------------

/**
 * Convenience: submit a one-click "thumbs up/down" record without going
 * through the full dialog. Used for the chat answer thumbs-up shortcut.
 */
export async function submitQuickSentiment(args: {
  sentiment: Sentiment
  sessionId?: string
  messageId?: string
  responseContent?: string | null
}): Promise<FeedbackSubmitResponse> {
  return submitFeedback({
    lane: "response_feedback",
    fields: { sentiment: args.sentiment },
    context: {
      session_id: args.sessionId,
      message_id: args.messageId,
      response_content: args.responseContent,
    },
  })
}
